import { z } from 'zod'
import { objectToCamel } from 'ts-case-convert'
import { clientSchema } from './client'
import * as GarmentSchemas from './garment' // avoid circular dependency
import { fabricHomeSchema } from './fabric'
import { delayStatusSchema, orderStatusSchema } from './status'
import { deliveryGarmentBoxSchema } from './shipment'
import * as PaymentSchemas from './payment' // avoid circular dependency
import { addressSchema } from './address'
import { dealerSchema } from './user'

//* ---------------- BASE ---------------- *//
export const baseOrderSchema = z.object({
  id: z.number(),
  dealer_id: z.number(),
  customer_id: z.number(),
  title: z.string(),
  custom_order_number: z.string().nullable(),
  garment_count: z.number(),
  ship_type: z.string(),
  ship_cost: z.string(),
  tariff_cost: z.string().nullable(),
  subtotal: z.string(),
  dealer_discount: z.string(),
  total_discount: z.string().nullable(),
  tax: z.string(),
  grand_total: z.string(),
  deposit_percentage: z.number(),
  current_balance: z.coerce.number(),
  measurement_units: z.string(),
  payment_status: z.string(),
  ordered_at: z.coerce.date().nullable(),
  created_at: z.coerce.date(),
})

const baseOrderHomeGarmentSchema = z.lazy(() =>
  GarmentSchemas.baseGarmentHomeSchema.extend({
    fabric: fabricHomeSchema,
    order_status: orderStatusSchema,
    delay_status: delayStatusSchema,
    shipment: deliveryGarmentBoxSchema.optional().nullable(),
    estimated_ship_date: z.coerce.date().nullable(),
  }),
)

const baseOrderTypeSchema = z.union([z.literal('MTM'), z.literal('MTO'), z.literal('TIE'), z.literal('Unknown')])

//* ---------------- SCHEMAS ---------------- *//
export const dealerOrderSchema = baseOrderSchema.transform(objectToCamel)
export const orderSchema = baseOrderSchema
  .extend({ currency: z.lazy(() => PaymentSchemas.currencySchema) })
  .transform(objectToCamel)

export const orderHomeGarmentSchema = baseOrderHomeGarmentSchema.transform(objectToCamel)

export const orderHomeSchema = baseOrderSchema
  .extend({
    order_type: baseOrderTypeSchema,
    customer: clientSchema,
    garments: z.array(orderHomeGarmentSchema),
  })
  .transform(objectToCamel)

export const orderExtendedSchema = baseOrderSchema
  .extend({
    order_type: baseOrderTypeSchema,
    order_source: z.string(),
    card_token: z.string().nullable(),
    currency: z.lazy(() => PaymentSchemas.currencySchema),
    shipping_address: addressSchema,
    dealer: dealerSchema,
    customer: clientSchema,
    garments: z.array(z.lazy(() => GarmentSchemas.garmentSchema)),
    dealer_charges: z.lazy(() => z.array(PaymentSchemas.chargeSchema)),
    outgoing_address_id: z.number().nullable().optional(), // admin only
    outgoing_address: addressSchema.nullable().optional(), // admin only
  })
  .transform(objectToCamel)

export const metricsSchema = z
  .object({
    delays: z.number(),
    incompletes: z.number(),
  })
  .transform(objectToCamel)

//* ---------------- TYPES ---------------- *//
declare global {
  namespace TrinityAPI {
    type OrderType = z.infer<typeof orderSchema>
    type DealerOrderType = z.infer<typeof dealerOrderSchema>
    type OrderHomeGarmentType = z.infer<typeof orderHomeGarmentSchema>
    type OrderHomeType = z.infer<typeof orderHomeSchema>
    type OrderExtendedType = z.infer<typeof orderExtendedSchema>
    type MetricsType = z.infer<typeof metricsSchema>
  }
}
